import React, { Fragment, useCallback, useEffect, useMemo } from "react";
import styled from "styled-components";
import * as AppActions from "../redux/actions/app.actions";
import { AppNotificationTypeEnum } from "../models/interfaces/IAppNotification";
import { ModalTypes } from "../components/modal/Modal";
import { PaginationTypes } from "@sellout/models/.dist/interfaces/IPagination";
import { Dropdown, Icons } from "@sellout/ui";
import CustomerTable from "../components/CustomerTable";
import PageLoader from "../components/PageLoader";
import useListCustomers from "../hooks/useListCustomers.hook";
import { TopLeftPaddedPage } from "../components/PageLayout";
import PageSearch from "../components/PageSearch";
import FilterButton from "../elements/FilterButton";
import { useMutation } from "@apollo/react-hooks";
import GENERATE_USER_PROFILE_REPORT from "@sellout/models/.dist/graphql/mutations/generateUserProfileReport.mutation";
import { media } from "@sellout/ui/build/utils/MediaQuery";
import { useDispatch } from "react-redux";
import { ContactsScreenEnum } from "@sellout/models/.dist/enums/ContactsScreenEnum";

const SearchContainer = styled.div`
  margin: 0px 24px 24px 0px;
  display: flex;
  gap: 10px;
  ${media.mobile`
   display: block;
  `};
`;
const TableContainer = styled.div`
  display: flex;
  height: calc(100% - 74px);
  ${media.mobile`
  height: calc(100% - 160px);
`};
`;
const FlexWrapper = styled.div`
  display: flex;
  gap: 10px;
   ${media.mobile`
  margin:4px;
`};
`;
const InnerBox = styled.div`
  width: 250px;
  ${media.mobile`
  min-width: 110px;
`};
`;
enum CustomerDropDownFiltersEnum {
  CustomersOrContacts = "Customers & Contacts",
  CustomersOnly = "Customers Only",
  ContactsOnly = "Contacts Only",
}

const ContactsScreenEnumToDisplayMap = {
  [ContactsScreenEnum.ShowContactsAndCustomer]: CustomerDropDownFiltersEnum.CustomersOrContacts,
  [ContactsScreenEnum.ShowCustomersOnly]: CustomerDropDownFiltersEnum.CustomersOnly,
  [ContactsScreenEnum.ShowContactsOnly]: CustomerDropDownFiltersEnum.ContactsOnly,
};

const DisplayToContactsScreenEnumMap = {
  [CustomerDropDownFiltersEnum.CustomersOrContacts]: ContactsScreenEnum.ShowContactsAndCustomer,
  [CustomerDropDownFiltersEnum.CustomersOnly]: ContactsScreenEnum.ShowCustomersOnly,
  [CustomerDropDownFiltersEnum.ContactsOnly]: ContactsScreenEnum.ShowContactsOnly,
};

type CustomerListProps = {};
const setDefaultCustomer = {
  screenType: ContactsScreenEnum.ShowContactsAndCustomer,
};
const PLACEHOLDER_TEXT = "Search by Name, Email, Phone";
const CustomerList: React.FC<CustomerListProps> = () => {
  /** Hooks */
  const [searchQuery, setSearchQuery] = React.useState("");
  const [disable, setDisable] = React.useState("false");
  const [customerDropdown, setCustomerDropdown] =
    React.useState(setDefaultCustomer);
  const query = {
    name: searchQuery,
    email: searchQuery,
    phoneNumber: searchQuery,
    any: true,
    screenType: customerDropdown?.screenType,
  };
  const dispatch = useDispatch();
  const [generateUserProfileReport, { loading }] = useMutation(
    GENERATE_USER_PROFILE_REPORT,
    {
      variables: {
        query,
      },
      onCompleted(data) {
        const { url = "" } = data.generateUserProfileReport;
        // TODO download with URL
        if (url) {
          window.location.href = url;
        } else if (data?.generateUserProfileReport?.message) {
          // TODO display success toaster in case of Email sent to client
          dispatch(
            AppActions.showNotification(
              data?.generateUserProfileReport?.message,
              AppNotificationTypeEnum.Success
            )
          );
        }
        setDisable("false");
      },
      onError(error) {
        console.error(error);
      },
    }
  );

  const {
    customers,
    loading: customersLoading,
    fetchMore,
    refetch,
  } = useListCustomers({
    variables: {
      pagination: {
        pageSize: 60,
        pageNumber: 1,
      },
      query,
    },
    context: {
      debounceKey: "QUERY_CUSTOMERS",
      debounceTimeout: 250,
    },
  });
  const dropdownItems = useMemo(
    () =>
      Object.entries(CustomerDropDownFiltersEnum).map(([, text]) => ({
        text,
        value: DisplayToContactsScreenEnumMap[text],
      })),
    []
  );

  const handleImportClick = useCallback(() => {
    dispatch(AppActions.pushModal(ModalTypes.ImportContactModal, { fetch: refetch }));
  }, [dispatch]);

  const handleDropdownChange = useCallback((selected: string) => {
    setCustomerDropdown({ screenType: selected as ContactsScreenEnum });
  }, []);
  useEffect(() => {
    if (typeof refetch === "function") {
      refetch();
    }
  }, [refetch]);
  /** Render */
  return (
    <Fragment>
      <PageLoader nav={true} fade={Boolean(customers)} />
      <TopLeftPaddedPage>
        <SearchContainer>
          <PageSearch
            setSearchQuery={setSearchQuery}
            searchQuery={searchQuery}
            loading={customersLoading}
            placeHolder={PLACEHOLDER_TEXT}
          />
          <FlexWrapper>
            <InnerBox>
              <Dropdown
                value={ContactsScreenEnumToDisplayMap[customerDropdown?.screenType]}
                width="100%"
                containerMinHeight={"48px"}
                optionsHeight={"48px"}
                items={dropdownItems}
                onChange={handleDropdownChange}
                itemContainerHeight={"52px"}
                valueWhiteSpace={"pre-wrap"}
              />
            </InnerBox>
            <FilterButton
              text="Export List"
              icon={Icons.DownloadReport}
              loading={loading}
              onClick={() => {
                if (disable == "false") {
                  setDisable("true");
                  generateUserProfileReport();
                }
              }}
            />
            <FilterButton
              text={"Import"}
              icon={Icons.UploadLight}
              onClick={handleImportClick}
            />
          </FlexWrapper>
        </SearchContainer>
        {customers ? (
          <TableContainer>
            <CustomerTable
              customers={customers}
              fetchMore={fetchMore}
              paginationType={PaginationTypes.Customers}
              screenType={customerDropdown?.screenType}
            />
          </TableContainer>
        ) : null}
      </TopLeftPaddedPage>
    </Fragment>
  );
};

export default CustomerList;
